import axios from 'axios';
import { useState } from 'react';
import { Spinner } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import { useUserAuth } from '../context/UserAuthContext';
import { FaRupeeSign, FaCheckCircle } from 'react-icons/fa';

const RequestLeadDeal = ({ item, dealCloseTrigger }) => {
    const { user, token } = useUserAuth();
    const merchant_id = user?.uid;
    const request_lead_id = item.id;
    const base_url = import.meta.env.VITE_LOCAL_BASE_URL;

    const [formData, setFormData] = useState({
        deal_close_price: '',
        payable_amount: 0,
        gst: 0,
        total_amount: 0,
        remark: ''
    });

    const [showDealModal, setShowDealModal] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [loading, setLoading] = useState({
        form: false,
        payment: false
    });

    const handleDealCloseChange = (e) => {
        const { name, value } = e.target;

        if (name === 'deal_close_price') {
            const dealClosePrice = parseFloat(value) || 0;
            const platformFee = item.platform_fee || 10;
            const totalCommission = (dealClosePrice * platformFee) / 100;
            const baseCommission = totalCommission / 1.18; // Removing GST
            const gstAmount = totalCommission - baseCommission;

            setFormData({
                deal_close_price: value,
                payable_amount: baseCommission,
                gst: gstAmount,
                total_amount: totalCommission,
                remark: `${platformFee}% of ₹${dealClosePrice} = ₹${totalCommission.toFixed(2)} (incl. GST)`
            });
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleDealCloseSubmit = async (e) => {
        e.preventDefault();
        if (!formData.deal_close_price || parseFloat(formData.deal_close_price) <= 0) {
            toast.error('Please enter a valid deal close price');
            return;
        }

        setLoading(prev => ({ ...prev, form: true }));

        try {
            await axios.put(`${base_url}/api/v1/deal-mgmt/vendor-enquiry-lead/${request_lead_id}`, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            });
            
            setShowDealModal(false);
            setShowPaymentModal(true);
            toast.success('Deal details saved. Proceed with payment.');
        } catch (error) {
            console.error('Error:', error);
            toast.error(error.response?.data?.message || 'Error updating deal details');
        } finally {
            setLoading(prev => ({ ...prev, form: false }));
        }
    };

    const processPayment = async () => {
        setLoading(prev => ({ ...prev, payment: true }));
        
        try {
            const orderId = uuidv4();
            const amount = Math.round(formData.total_amount);

            const paymentReq = {
                order_id: orderId,
                amount: amount,
                currency: "INR",
                payment_capture: 1
            };

            const result = await axios.post(`${base_url}/api/v1/razorpay/create`, paymentReq, {
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            });

            const orderData = result?.data?.data;
            if (!orderData) {
                throw new Error('Payment initialization failed');
            }

            const options = {
                key: import.meta.env.VITE_RAZORPAY_KEY || "rzp_live_6XXCRghG3I98Mm",
                amount: orderData.amount,
                currency: orderData.currency,
                name: "Event Planet",
                description: "Vendor Enquiry Lead Payment",
                image: "https://eventplanet.in/assets/home/logo_main.png",
                order_id: orderData.id,
                handler: async (response) => {
                    try {
                        // Save transaction
                        await axios.post(`${base_url}/api/v1/transaction`, {
                            merchant_id,
                            transactionType: 'credit',
                            transaction_id: response?.razorpay_payment_id,
                            amount: formData.total_amount,
                            info: {
                                base_price: formData.payable_amount,
                                cgst: formData.gst / 2,
                                sgst: formData.gst / 2,
                                sub_total: formData.total_amount,
                                discount: 0,
                                grand_total: formData.total_amount
                            },
                        }, {
                            headers: {
                                Authorization: `Bearer ${token}`,
                            }
                        });

                        // Update payment status
                        await axios.post(`${base_url}/api/v1/requests/payment/status`, {
                            id: request_lead_id
                        }, {
                            headers: {
                                Authorization: `Bearer ${token}`,
                            }
                        });

                        toast.success('Payment successful! Deal closed.');
                        setShowPaymentModal(false);
                        dealCloseTrigger();
                    } catch (error) {
                        console.error('Payment processing error:', error);
                        toast.error('Error processing payment');
                    }
                },
                theme: { color: "#4154f1" },
                modal: {
                    ondismiss: () => {
                        toast.info('Payment window closed');
                    }
                }
            };

            const paymentObj = new window.Razorpay(options);
            paymentObj.open();
        } catch (error) {
            toast.error(`Payment error: ${error.message}`);
            console.error('payment error', error);
        } finally {
            setLoading(prev => ({ ...prev, payment: false }));
        }
    };

    return (
        <>
            <Button
                variant="primary"
                size="sm"
                className="close-deal-btn"
                onClick={() => setShowDealModal(true)}
            >
                Close Deal
            </Button>

            {/* Close Deal Modal */}
            <Modal show={showDealModal} onHide={() => setShowDealModal(false)} centered>
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="text-primary fw-bold">Close Deal</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleDealCloseSubmit}>
                    <Modal.Body className="pt-3">
                        <div className="deal-form">
                            <Form.Group className="mb-4">
                                <Form.Label className="fw-semibold">Deal Close Price (₹)</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="deal_close_price"
                                    value={formData.deal_close_price}
                                    onChange={handleDealCloseChange}
                                    placeholder="Enter final deal amount"
                                    className="py-2"
                                    min="0"
                                    step="0.01"
                                    required
                                    autoFocus
                                />
                                <Form.Text className="text-muted">
                                    Platform Fee: {item.platform_fee || 10}% (inclusive of GST)
                                </Form.Text>
                            </Form.Group>

                            {formData.deal_close_price && (
                                <div className="calculation-preview bg-light p-3 rounded mb-3">
                                    <h6 className="mb-3">Fee Calculation</h6>
                                    <div className="d-flex justify-content-between mb-2">
                                        <span>Platform Fee ({item.platform_fee || 10}%):</span>
                                        <span className="fw-medium">₹{formData.payable_amount.toFixed(2)}</span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-2">
                                        <span>GST (18%):</span>
                                        <span className="fw-medium">₹{formData.gst.toFixed(2)}</span>
                                    </div>
                                    <hr className="my-2" />
                                    <div className="d-flex justify-content-between fw-bold">
                                        <span>Total Payable:</span>
                                        <span className="text-primary">₹{formData.total_amount.toFixed(2)}</span>
                                    </div>
                                </div>
                            )}

                            <Form.Group className="mb-3">
                                <Form.Label className="fw-semibold">Remark (Optional)</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    name="remark"
                                    value={formData.remark}
                                    onChange={handleDealCloseChange}
                                    placeholder="Add any notes about this deal"
                                />
                            </Form.Group>
                        </div>
                    </Modal.Body>
                    <Modal.Footer className="border-0 pt-0">
                        <Button 
                            variant="outline-secondary" 
                            onClick={() => setShowDealModal(false)}
                            className="px-4"
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            type="submit"
                            disabled={!formData.deal_close_price || loading.form}
                            className="px-4"
                        >
                            {loading.form ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Saving...
                                </>
                            ) : 'Save & Continue'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Payment Modal */}
            <Modal show={showPaymentModal} onHide={() => setShowPaymentModal(false)} centered>
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="text-primary fw-bold">Complete Payment</Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-3">
                    <div className="payment-summary">
                        <div className="text-center mb-4">
                            <div className="payment-icon mb-3">
                                <FaRupeeSign size={40} className="text-primary" />
                            </div>
                            <h5>Payable Amount</h5>
                            <h2 className="text-primary fw-bold mb-0">
                                ₹{formData.total_amount?.toFixed(2)}
                            </h2>
                        </div>

                        <div className="payment-breakdown bg-light p-3 rounded">
                            <h6 className="mb-3">Payment Breakdown</h6>
                            <div className="d-flex justify-content-between mb-2">
                                <span>Base Commission:</span>
                                <span>₹{formData.payable_amount?.toFixed(2)}</span>
                            </div>
                            <div className="d-flex justify-content-between mb-2">
                                <span>CGST (9%):</span>
                                <span>₹{(formData.gst / 2)?.toFixed(2)}</span>
                            </div>
                            <div className="d-flex justify-content-between mb-2">
                                <span>SGST (9%):</span>
                                <span>₹{(formData.gst / 2)?.toFixed(2)}</span>
                            </div>
                            <hr />
                            <div className="d-flex justify-content-between fw-bold">
                                <span>Total:</span>
                                <span>₹{formData.total_amount?.toFixed(2)}</span>
                            </div>
                        </div>

                        <div className="info-box bg-primary-soft p-3 rounded mt-3">
                            <FaCheckCircle className="text-primary me-2" />
                            <small>By proceeding with payment, you confirm the deal closure</small>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0">
                    <Button 
                        variant="outline-secondary" 
                        onClick={() => setShowPaymentModal(false)}
                        className="px-4"
                    >
                        Later
                    </Button>
                    <Button
                        variant="success"
                        onClick={processPayment}
                        disabled={loading.payment}
                        className="px-4"
                    >
                        {loading.payment ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Processing...
                            </>
                        ) : (
                            `Pay ₹${formData.total_amount?.toFixed(2)}`
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default RequestLeadDeal;